import { ActionIcon, Group, Stack, Text, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { CSSProperties } from "react";

import SvgIconRaw from "~/components/SvgIconRaw";
import { Message } from "~/utils/Types";

import SmallButton from "./Button/SmallButton";
import ServerMessage from "./ServerMessage";

type ResponsiveSize =
  | number
  | string
  | {
      xs?: number | string;
      sm?: number | string;
      md?: number | string;
      lg?: number | string;
      xl?: number | string;
    };

type Props = {
  children: React.ReactNode;
  title?: string;
  description?: string;
  cancelText?: string;
  onCancel?: (...args: unknown[]) => void;
  displayCancelButton?: boolean;
  confirmText?: string;
  onConfirm?: (...args: unknown[]) => void;
  disableConfirmButton?: boolean;
  hideConfirmButton?: boolean;
  loading?: boolean;
  message?: Message;
  minWidth?: ResponsiveSize;
  maxWidth?: ResponsiveSize;
  /**
   * Fixed dialog width. Use for chart dialogs: charts measure their container
   * on mount, and an auto-sized modal gives them an unstable width.
   */
  width?: ResponsiveSize;
  bgcolor?: string;
  buttonsAlignment?: "flex-end" | "center";
};

// MUI breakpoint px values (sm 600 / md 960 / lg 1440 / xl 1920) — consumers
// pass both responsive objects ({ sm: 500 }) and breakpoint keywords ("md").
const MUI_BREAKPOINT_PX: Record<string, number> = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1440,
  xl: 1920,
};

const BREAKPOINT_ORDER = ["xs", "sm", "md", "lg", "xl"] as const;

const useResolvedSize = (
  value: ResponsiveSize | undefined,
): number | string | undefined => {
  const mediaQueryOptions = { getInitialValueInEffect: false } as const;
  const sm = useMediaQuery("(min-width: 37.5em)", true, mediaQueryOptions);
  const md = useMediaQuery("(min-width: 60em)", true, mediaQueryOptions);
  const lg = useMediaQuery("(min-width: 90em)", true, mediaQueryOptions);
  const xl = useMediaQuery("(min-width: 120em)", true, mediaQueryOptions);

  if (value === undefined) return undefined;
  if (typeof value === "string") {
    return MUI_BREAKPOINT_PX[value] ?? value;
  }
  if (typeof value === "number") return value;

  const current = xl ? 4 : lg ? 3 : md ? 2 : sm ? 1 : 0;
  for (let i = current; i >= 0; i--) {
    const candidate = value[BREAKPOINT_ORDER[i]];
    if (candidate !== undefined) return candidate;
  }
  return undefined;
};

const DialogWrapper = ({
  title,
  description,
  children,
  cancelText,
  onCancel,
  displayCancelButton,
  confirmText,
  onConfirm,
  disableConfirmButton,
  hideConfirmButton,
  loading,
  message,
  minWidth,
  maxWidth,
  width,
  bgcolor,
  buttonsAlignment,
}: Props) => {
  const resolvedMinWidth = useResolvedSize(minWidth ?? { xs: 300, md: 600 });
  const resolvedMaxWidth = useResolvedSize(maxWidth);
  const resolvedWidth = useResolvedSize(width);

  return (
    <Stack
      h="100%"
      w={resolvedWidth ?? "100%"}
      miw={resolvedMinWidth}
      maw={resolvedMaxWidth}
      justify="center"
      pos="relative"
      gap={0}
    >
      <Tooltip label="close">
        <ActionIcon
          aria-label="Close"
          onClick={onCancel}
          variant="subtle"
          color="gray"
          radius="xl"
          w={26}
          h={26}
          miw={26}
          mih={26}
          pos="absolute"
          top={-4}
          right={-4}
          style={{
            zIndex: 2,
            backgroundColor: "var(--mantine-color-bg-secondary)",
          }}
        >
          <SvgIconRaw name="XClose" size={18} />
        </ActionIcon>
      </Tooltip>
      <Stack
        m={8}
        p={16}
        gap={16}
        h="100%"
        bg={bgcolor || "var(--mantine-color-bg-secondary)"}
        style={{
          border: "1px solid var(--mantine-color-border-secondary)",
          borderRadius: "0.3rem",
          overflow: "auto",
        }}
      >
        {title && (
          <Text fz="xl" fw={600}>
            {title}
          </Text>
        )}
        <Stack
          gap={16}
          h="100%"
          mah="90vh"
          pos="relative"
          style={{ overflow: "auto" }}
        >
          {description && (
            <Text c="var(--mantine-color-text-secondary)">{description}</Text>
          )}
          {children}
          {message && message.text && <ServerMessage message={message} />}
        </Stack>
        {((displayCancelButton && onCancel) ||
          (!hideConfirmButton && onConfirm)) && (
          <Group
            gap={8}
            p={16}
            style={{
              alignSelf: (buttonsAlignment ||
                "flex-end") as CSSProperties["alignSelf"],
            }}
          >
            {displayCancelButton && onCancel && (
              <SmallButton
                title={cancelText || "Cancel"}
                onClick={onCancel}
                variant="outline"
              />
            )}
            {!hideConfirmButton && onConfirm && (
              <SmallButton
                title={confirmText || "Confirm"}
                onClick={onConfirm}
                variant="filled"
                loading={loading}
                disabled={disableConfirmButton}
              />
            )}
          </Group>
        )}
      </Stack>
    </Stack>
  );
};

export default DialogWrapper;
